import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Wishlist = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { addToCart } = useCart();
  const [wishlist, setWishlist] = useState({ products: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      fetchWishlist();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`);
      setWishlist(response.data);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/remove/${productId}`);
      setWishlist(prev => ({
        ...prev,
        products: prev.products.filter(p => p.id !== productId)
      }));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleAddToCart = async (product) => {
    if (product.size?.length > 0) {
      const success = await addToCart(product.id, product.size[0]);
      if (success) {
        await handleRemove(product.id);
      }
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-concrete-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-chicago-red border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-concrete-white" data-testid="wishlist-page">
      {/* Header */}
      <div className="bg-deep-obsidian py-16">
        <div className="max-w-7xl mx-auto px-6">
          <p className="font-bebas text-chicago-red text-lg tracking-widest">MY ACCOUNT</p>
          <h1 className="font-anton text-4xl md:text-5xl text-white tracking-tighter uppercase">WISHLIST</h1>
          <p className="text-cement-grey mt-2">{wishlist.products.length} items saved</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {wishlist.products.length === 0 ? (
          <div className="bg-white border border-gray-200 p-12 text-center">
            <Heart className="w-12 h-12 text-cement-grey mx-auto mb-4" />
            <p className="font-bebas text-xl text-cement-grey">YOUR WISHLIST IS EMPTY</p>
            <p className="text-cement-grey mt-2">Save items you love to your wishlist</p>
            <Link to="/products">
              <Button className="mt-6 bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
                Browse Collection
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlist.products.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-gray-200 overflow-hidden group"
                data-testid={`wishlist-item-${product.id}`}
              >
                <Link to={`/products/${product.id}`} className="block">
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/400'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>
                <div className="p-4">
                  <p className="text-cement-grey text-xs uppercase tracking-wider">{product.brand}</p>
                  <Link to={`/products/${product.id}`}>
                    <h3 className="font-bold hover:text-chicago-red transition-colors line-clamp-1">
                      {product.name}
                    </h3>
                  </Link>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bebas text-xl">${product.price.toFixed(2)}</span>
                    <span className="text-xs text-cement-grey uppercase">{product.era}</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 bg-deep-obsidian hover:bg-deep-obsidian/90 text-white text-xs uppercase tracking-widest font-bold py-3 rounded-sm"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      Add to Cart
                    </Button>
                    <Button
                      onClick={() => handleRemove(product.id)}
                      variant="outline"
                      className="px-3 border-2 border-gray-200 hover:border-chicago-red hover:text-chicago-red rounded-sm"
                      data-testid={`remove-wishlist-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
